import { EditOutlined, LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Table } from 'antd'
import { MenuProps } from 'antd/lib'
import { ColumnsType } from 'antd/lib/table'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useStocksServiceDeleteFormEndpoint,
  useStocksServiceGetFormListEndpoint,
  useStocksServiceGetFormListEndpointKey
} from 'src/api/queries'
import { ApiError, GetFormListResponseItem } from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  SearchBar,
  TableActionButtons
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, resetPagination } from 'src/utils'

import { PharmaceuticalFormModal } from './PharmaceuticalFormModal'

export const PharmaceuticalFormsCard = () => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const pagination = useTablePagination()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [currentId, setCurrentId] = useState<string>()
  const [isOpen, setIsOpen] = useState(false)

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(!!search && {
      searchTerm: search
    })
  }

  const { data, isLoading } = useStocksServiceGetFormListEndpoint(queryParams, [
    queryParams
  ])

  const { mutate } = useStocksServiceDeleteFormEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetFormListEndpointKey]
      })
      message.success(t('medicine_register.pharmaceutical_form.delete_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const getActionItems = useMemo(
    () =>
      (id: string): MenuProps['items'] => {
        return [
          {
            key: 'edit',
            label: t('common.edit'),
            icon: <EditOutlined />,
            onClick: () => {
              onToggle()
              setCurrentId(id)
            }
          },
          {
            key: 'divider',
            type: 'divider'
          },
          {
            key: 'delete',
            label: (
              <DeleteConfirm
                title={t('medicine_register.pharmaceutical_form.delete')}
                onConfirm={() => mutate({ id })}
              />
            ),
            danger: true
          }
        ]
      },
    [t]
  )

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onToggle = () => setIsOpen((prev) => !prev)

  const columns: ColumnsType<GetFormListResponseItem> = [
    {
      key: 'name',
      title: t('medicine_register.pharmaceutical_form.title'),
      dataIndex: 'name'
    },
    {
      key: 'actions',
      width: 64,
      render: ({ id }) => (
        <TableActionButtons items={getActionItems(id)} justify="center" />
      )
    }
  ]

  return (
    <Card>
      <Flex vertical gap={16}>
        <Flex gap={16}>
          <Button
            icon={<LeftOutlined />}
            onClick={() => navigate(paths.medicinesRegister.root)}
          >
            {t('common.back')}
          </Button>

          <SearchBar className="flex-1" onSearch={onSearch} />

          <Button type="primary" onClick={onToggle}>
            {t('common.add')}
          </Button>
        </Flex>

        <Table
          rowKey="id"
          bordered
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>

      {isOpen && (
        <PharmaceuticalFormModal
          id={currentId}
          onCancel={() => {
            onToggle()
            setCurrentId(undefined)
          }}
        />
      )}
    </Card>
  )
}
